import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { uniqBy } from 'lodash-es'

import { useNotification } from '../../../../hooks'
import { getItemsFromIdwellApiResponse } from '../../../../utils/api'
import { FormModal, formValidationSchemaNoChanged } from '../../../Form'
import { MODAL_HEADER_VARIANT } from '../../../Modal'
import { toUserSelectItemsWithoutStatus } from '../../../User'
import { getEmailAvailableUserProfiles } from '../../Email.api'
import {
  EMAIL_COPY_TO_ENTITY_MODAL_FIELD_NAME_USER,
  EMAIL_COPY_TO_ENTITY_MODAL_INITIAL_VALUES,
} from '../EmailCopyToEntityModal.constants'
import { EmailCopyToEntityModalFields } from '../EmailCopyToEntityModalFields'

function EmailCopyToEntityFormModal({
  getPayload,
  getValidationSchema = formValidationSchemaNoChanged,
  initialFormValues = EMAIL_COPY_TO_ENTITY_MODAL_INITIAL_VALUES,
  initialIds,
  titleKey = 'Email.CopyToEntityModalTitleKey',
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification } = useNotification()

  const [modal, setModal] = useState(null)

  const [isInitiating, setInitiating] = useState(initialIds?.length > 0)

  const [initialValues, setInitialValues] = useState(initialFormValues)

  const validationSchema = getValidationSchema({
    initialValues: initialFormValues, // initialFormValues for noChanged notification if there is no pre-filled data from the backend
  })

  function handleCloseModal() {
    setModal(null)
  }

  async function handleGetAvailableUserProfiles(params) {
    return getEmailAvailableUserProfiles(params).then(response => {
      const items = getItemsFromIdwellApiResponse(response)

      return toUserSelectItemsWithoutStatus(items)
    })
  }

  async function handleUpdateAvailableUserProfiles({ setValues, values }) {
    return handleGetAvailableUserProfiles(values).then(items => {
      setValues(prev => {
        return {
          ...prev,
          [EMAIL_COPY_TO_ENTITY_MODAL_FIELD_NAME_USER]: uniqBy(
            [...prev[EMAIL_COPY_TO_ENTITY_MODAL_FIELD_NAME_USER], ...items],
            'value'
          ),
        }
      })
    })
  }

  function handleInitAvailableUserProfiles() {
    if (initialIds?.length > 0) {
      handleGetAvailableUserProfiles({ id: initialIds })
        .then(data => {
          setInitialValues(prev => ({
            ...prev,
            [EMAIL_COPY_TO_ENTITY_MODAL_FIELD_NAME_USER]: data,
          }))
        })
        .catch(() => {
          handleAddErrorNotification('Error.CommonApiRequestFailKey')
        })
        .finally(() => {
          setInitiating(false)
        })
    }
  }

  function handleSubmit({ value }) {
    onSubmit(value)
  }

  useEffect(() => {
    handleInitAvailableUserProfiles()
  }, [])

  return (
    <FormModal
      Fields={EmailCopyToEntityModalFields}
      containerProps={{ headerVariant: MODAL_HEADER_VARIANT.EDIT, modal }}
      formBodyProps={{
        submitButtonText: t('Email.EmailCopyToEntityModalSubmitBtnKey'),
        withNotifications: true,
      }}
      formFieldsProps={{
        initialIds,
        isLoading: isInitiating,
        setModal,
        onClose: handleCloseModal,
        onUpdateAvailableUserProfiles: handleUpdateAvailableUserProfiles,
      }}
      formTitle={titleKey}
      getPayload={getPayload}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onClose={onClose}
      onSubmit={handleSubmit}
    />
  )
}

export default EmailCopyToEntityFormModal
